/* Generic Partner Login (Phase PP-2/3/4 · Feb 2026).
   Theme-able login page shared by lender / insurance / carrier portals.
   Banking has its own variant (BankingLogin.jsx) shipped first.

   Props:
     • portalKey   — 'lender' | 'insurance' | 'carrier' (used for testid + redirect)
     • role        — 'lender_partner' | …
     • portalName  — display label
     • portalIcon  — lucide icon
     • theme       — colour family ('violet' / 'emerald' / 'amber')
     • tagline     — value-prop headline (string)
     • proofs      — array of {icon, label}
*/
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

// Static class map — Tailwind JIT needs literal class names in source.
const THEME = {
  sky:     { grad: "from-slate-900 to-sky-900",     bgIcon: "bg-sky-500/20",     text: "text-sky-300",     mobile: "text-sky-600",     bullet: "text-sky-300" },
  violet:  { grad: "from-slate-900 to-violet-900",  bgIcon: "bg-violet-500/20",  text: "text-violet-300",  mobile: "text-violet-600",  bullet: "text-violet-300" },
  emerald: { grad: "from-slate-900 to-emerald-900", bgIcon: "bg-emerald-500/20", text: "text-emerald-300", mobile: "text-emerald-600", bullet: "text-emerald-300" },
  amber:   { grad: "from-slate-900 to-amber-900",   bgIcon: "bg-amber-500/20",   text: "text-amber-300",   mobile: "text-amber-600",   bullet: "text-amber-300" },
};

export default function PartnerLogin({
  portalKey, role, portalName, portalIcon: Icon, theme, tagline, proofs,
}) {
  const { login, logout } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const tc = THEME[theme] || THEME.sky;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u.role !== role && u.role !== "admin") {
        await logout();
        toast.error(`This portal is for ${portalName.toLowerCase()} partners only.`);
        return;
      }
      toast.success(`Welcome, ${u.name || u.email}`);
      nav(`/${portalKey}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`${portalKey}-login`} className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className={`hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br ${tc.grad} text-slate-100`}>
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-md ${tc.bgIcon} grid place-items-center`}>
            <Icon className={`w-5 h-5 ${tc.text}`} />
          </div>
          <div>
            <div className="font-semibold">verqo · {portalName}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Partner Portal</div>
          </div>
        </div>
        <div className="space-y-6">
          <h1 className="display text-4xl leading-tight tracking-tight">{tagline}</h1>
          <div className="space-y-3 text-sm text-slate-300">
            {proofs.map(({ icon: I, label }, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <I className={`w-4 h-4 ${tc.bullet} mt-0.5`} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="text-[11px] text-slate-400">© verqo · Partner portal</div>
      </div>

      <div className="flex flex-col items-center justify-center p-8 lg:p-14">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4">
          <div className={`flex items-center gap-2 lg:hidden mb-3`}>
            <Icon className={`w-5 h-5 ${tc.mobile}`} />
            <div className="font-semibold">verqo {portalName}</div>
          </div>
          <div>
            <h2 className="display text-2xl tracking-tight">Sign in</h2>
            <p className="text-sm text-slate-600 mt-1">Use the email + temporary password your verqo contact sent you.</p>
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid={`${portalKey}-login-email`} autoComplete="email" required />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} data-testid={`${portalKey}-login-password`} autoComplete="current-password" required />
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid={`${portalKey}-login-submit`}>
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <div className="text-[11px] text-slate-500 text-center">
            Lost your password? Contact your verqo partnerships lead.
          </div>
        </form>
      </div>
    </div>
  );
}
